
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { companyFacts, mediaQuotes } from './constants';
import { Star, Quote } from 'lucide-react';

const AboutSection = () => {
  return (
    <section className="space-y-16">
      <div className="text-center">
        <h2 className="text-4xl font-montserrat font-bold text-[#894D1D] mb-4">
          About Cats About Town Tours
        </h2>
        <div className="w-24 h-1 bg-gradient-to-r from-[#894D1D] to-[#698235] mx-auto rounded-full"></div>
      </div>
      
      <div className="grid lg:grid-cols-3 gap-8">
        {/* Main Story - spans 2 columns */}
        <div className="lg:col-span-2">
          <Card className="h-full bg-gradient-to-br from-white to-[#F7F0DE]/30 border-[#894D1D]/10 shadow-xl">
            <CardContent className="p-10">
              <div className="prose prose-xl max-w-none">
                <p className="text-xl leading-relaxed mb-8 font-montserrat text-[#894D1D]/90">
                  Cats About Town Tours is New York City's first and only walking tour company 
                  dedicated to the real cats of the city's past. Founded in August 2023 by historian 
                  Peggy Gavan and producer Dan Rimada, each tour is based on original historical 
                  research and tells true stories of legendary working cats, heroic rescues, and 
                  overlooked feline figures.
                </p>
                
                <p className="text-xl leading-relaxed font-montserrat text-[#894D1D]/90">
                  Our current routes include Brooklyn Heights, the Financial District, and the Lower East Side. 
                  Tours are led by a team of three licensed NYC guides, with all historical content 
                  curated and researched by Peggy Gavan.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Quick Facts Sidebar */}
        <div>
          <Card className="bg-[#894D1D] text-white shadow-xl">
            <CardContent className="p-8">
              <h3 className="text-2xl font-montserrat font-semibold mb-6 text-center">
                At a Glance
              </h3>
              <div className="space-y-4">
                {companyFacts.slice(0, 6).map((fact, index) => (
                  <div key={index} className="border-b border-white/20 pb-3 last:border-b-0">
                    <div className="text-sm opacity-80 font-montserrat uppercase tracking-wide">
                      {fact.label}
                    </div>
                    <div className="text-white font-montserrat font-medium text-sm mt-1">
                      {fact.value}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Media Quotes Section */}
      <div className="space-y-8">
        <div className="text-center">
          <h3 className="text-3xl font-montserrat font-bold text-[#894D1D] mb-4">
            Media Coverage
          </h3>
          <div className="w-16 h-1 bg-gradient-to-r from-[#894D1D] to-[#698235] mx-auto rounded-full"></div>
        </div>
        
        <div className="grid md:grid-cols-2 gap-8">
          {mediaQuotes.map((quote, index) => (
            <Card key={index} className="bg-white border-l-4 border-l-[#698235] shadow-lg hover:shadow-xl transition-shadow duration-300">
              <CardContent className="p-8">
                <div className="flex items-start gap-4">
                  <Quote className="h-8 w-8 text-[#698235] flex-shrink-0 mt-1" />
                  <div>
                    <blockquote className="text-lg font-montserrat text-[#894D1D] italic mb-4 leading-relaxed">
                      "{quote.quote}"
                    </blockquote>
                    <div className="text-sm text-[#894D1D]/70 font-montserrat">
                      <div className="font-semibold text-[#698235]">{quote.source}</div>
                      <div>{quote.publication} • {quote.context}</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Press Mentions */}
      <div className="bg-gradient-to-r from-[#F7F0DE] to-white rounded-3xl p-10">
        <h3 className="text-3xl font-montserrat font-bold text-[#894D1D] mb-8 text-center">
          Featured In
        </h3>
        <div className="grid md:grid-cols-2 gap-6">
          {[
            { outlet: "NBC New York", coverage: "Broadcast segment", year: "2024" },
            { outlet: "The Guardian", coverage: "Brooklyn Heights tour feature", year: "2024" },
            { outlet: "People Magazine", coverage: "Unique cat tours spotlight", year: "2024" },
            { outlet: "amNY", coverage: "NYC's first cat walking tours", year: "2024" }
          ].map((mention, index) => (
            <div key={index} className="flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm border border-[#894D1D]/10">
              <div className="w-3 h-3 bg-gradient-to-r from-[#894D1D] to-[#698235] rounded-full flex-shrink-0"></div>
              <div>
                <div className="font-semibold text-[#894D1D] font-montserrat">{mention.outlet}</div>
                <div className="text-sm text-[#894D1D]/70 font-montserrat">{mention.coverage} • {mention.year}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
